<script setup>
import { ref, computed } from 'vue'
import ChartView from '@/components/charts/ChartView.vue'
import DetailDrawer from '@/components/shell/DetailDrawer.vue'
import { store } from '@/store/appStore.js'
const tierFilter = ref('All')
const CEIL = { A:5000, B:3000, C:1500 }
const divisionBySku = computed(() => Object.fromEntries(store.data.products.value.map(p => [p.sku, p.division])))

// BRD Module 8 asks for Monthly/Quarterly/Annual Purchase Value and Growth %.
// Per-pharmacy order history is real but sparse (~10 orders/month spread
// across 22 pharmacies), so a calendar-month SPOT value would read as $0 for
// most pharmacies most months — not a broken chart, just a misleadingly thin
// slice. Trailing-window rollups (last 1 / 3 / 12 months) are the standard
// BI reading of these terms and don't have that problem. Growth % compares
// the trailing quarter to the one before it.
const MONTHS = computed(() => [...new Set(store.data.orders.value.map(o => o.orderDate.slice(0,7)))].sort())
const last1 = computed(() => MONTHS.value.slice(-1))
const last3 = computed(() => MONTHS.value.slice(-3))
const prior3 = computed(() => MONTHS.value.slice(-6, -3))

function sumInMonths(orders, months) {
  const set = new Set(months)
  return Math.round(orders.filter(o => set.has(o.orderDate.slice(0,7))).reduce((s,o) => s + o.orderValue, 0))
}

const enriched = computed(() => {
  const byPh={}; store.data.orders.value.forEach(o=>{byPh[o.customerId]=(byPh[o.customerId]||0)+o.orderValue})
  return store.data.pharmacies.value.map(p => {
    const pv = Math.round((CEIL[p.category]||2000)*(p.potentialScore/100))
    const phOrders = store.data.orders.value.filter(o=>o.customerId===p.customerId)
    const purchaseValue = Math.round(byPh[p.customerId]||0)
    const avgOrderValue = phOrders.length ? Math.round(purchaseValue / phOrders.length) : 0
    const byDivision = {}
    phOrders.forEach(o => {
      const div = divisionBySku.value[o.productSku] || 'Unknown'
      byDivision[div] = (byDivision[div] || 0) + o.orderValue
    })
    const mixEntries = Object.entries(byDivision).sort((a,b)=>b[1]-a[1])
    const topDivision = mixEntries[0]?.[0] || '—'
    const topDivisionPct = purchaseValue && mixEntries[0] ? Math.round(mixEntries[0][1]/purchaseValue*100) : 0

    const monthlyValue = sumInMonths(phOrders, last1.value)
    const quarterlyValue = sumInMonths(phOrders, last3.value)
    const annualValue = purchaseValue // all 12 months already in scope
    const priorQuarterValue = sumInMonths(phOrders, prior3.value)
    // null = no prior-period baseline to compare against ("New" in the UI),
    // distinct from 0% growth (had activity both periods, no change).
    const growthPct = priorQuarterValue > 0
      ? Math.round((quarterlyValue - priorQuarterValue) / priorQuarterValue * 100)
      : (quarterlyValue > 0 ? null : 0)

    // Real 12-month trend for this specific pharmacy — genuine data, not the
    // "annualized run-rate" approximation POB's timeframe toggle uses,
    // because we now have actual monthly history to plot per pharmacy.
    const monthlySeries = MONTHS.value.map(m => sumInMonths(phOrders, [m]))

    return {
      ...p,
      purchaseValue,
      orderCount: phOrders.length,
      avgOrderValue,
      divisionCount: mixEntries.length,
      topDivision,
      topDivisionPct,
      potentialValue:pv,
      opportunityGapValue:Math.max(0,pv-purchaseValue),
      monthlyValue, quarterlyValue, annualValue, growthPct, monthlySeries,
    }
  }).sort((a,b)=>b.opportunityGapValue-a.opportunityGapValue)
})
const filtered = computed(() => tierFilter.value==='All' ? enriched.value : enriched.value.filter(p=>p.potentialClassification===tierFilter.value))
const segCounts = computed(() => { const c={Platinum:0,Gold:0,Silver:0,Bronze:0}; enriched.value.forEach(p=>c[p.potentialClassification]++); return c })
const segChart = computed(() => ({ labels:Object.keys(segCounts.value), datasets:[{ data:Object.values(segCounts.value), backgroundColor:['#5B4E9E','#C99A2E','#8B959B','#92583A'] }] }))

// Opportunity Gap Analysis: Actual Purchase Volume vs Estimated Potential,
// top 10 pharmacies by gap (already the default sort order of `enriched`).
const gapChart = computed(() => {
  const top10 = enriched.value.slice(0, 10)
  return {
    labels: top10.map(p => p.pharmacyName),
    datasets: [
      { label: 'Actual (USD)', data: top10.map(p => p.purchaseValue), backgroundColor: '#0F3D3E' },
      { label: 'Potential (USD)', data: top10.map(p => p.potentialValue), backgroundColor: '#C99A2E' },
    ]
  }
})

function growthTone(pct) {
  if (pct === null) return { cls: '', label: 'New' }
  if (pct > 0) return { cls: 'color:var(--status-green);font-weight:700;', label: `+${pct}%` }
  if (pct < 0) return { cls: 'color:var(--status-red);font-weight:700;', label: `${pct}%` }
  return { cls: 'color:var(--color-text-muted);', label: '0%' }
}

// Per-pharmacy drawer — the module previously had no drill-down at all,
// unlike every other master screen in the app.
const drawerCustomerId = ref(null)
const drawerPharmacy = computed(() => drawerCustomerId.value ? enriched.value.find(p => p.customerId === drawerCustomerId.value) || null : null)
const openDrawer = p => { drawerCustomerId.value = p.customerId }
const trendChart = computed(() => drawerPharmacy.value ? {
  labels: MONTHS.value.map(m => m.slice(5)),
  datasets: [{ label: 'Purchase Value (USD)', data: drawerPharmacy.value.monthlySeries, borderColor: '#0F3D3E', backgroundColor: 'rgba(15,61,62,0.08)', tension: 0.3, fill: true, pointRadius: 3 }]
} : { labels: [], datasets: [] })
</script>
<template>
  <div>
    <div class="card module-header"><h2>Segmentation across {{ enriched.length }} pharmacies</h2></div>
    <div class="split-side-main" style="margin-bottom:16px;">
      <div class="card" style="padding:18px;"><div class="eyebrow" style="margin-bottom:8px;">Segmentation</div><ChartView type="doughnut" :labels="segChart.labels" :datasets="segChart.datasets" :height="200" key="seg-donut" /></div>
      <div class="stat-row" style="margin-bottom:0;">
        <div class="card stat-card"><div class="stat-value" style="color:#5B4E9E;">{{ segCounts.Platinum }}</div><div class="stat-label">Platinum</div></div>
        <div class="card stat-card"><div class="stat-value" style="color:#97731f;">{{ segCounts.Gold }}</div><div class="stat-label">Gold</div></div>
        <div class="card stat-card"><div class="stat-value" style="color:#5B6469;">{{ segCounts.Silver }}</div><div class="stat-label">Silver</div></div>
        <div class="card stat-card"><div class="stat-value" style="color:#92583A;">{{ segCounts.Bronze }}</div><div class="stat-label">Bronze</div></div>
      </div>
    </div>
    <div class="card" style="padding:18px 20px;margin-bottom:16px;">
      <div class="eyebrow" style="margin-bottom:8px;">Opportunity Gap Analysis — Actual vs. Potential (top 10 by gap)</div>
      <ChartView type="bar" :labels="gapChart.labels" :datasets="gapChart.datasets" :height="280" key="gap-bar" />
    </div>
    <div class="card">
      <div class="toolbar">
        <select class="filter-select" v-model="tierFilter"><option>All</option><option>Platinum</option><option>Gold</option><option>Silver</option><option>Bronze</option></select>
        <span class="result-count">{{ filtered.length }} pharmacies</span>
      </div>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>Pharmacy</th><th>Territory</th><th>Cat.</th><th>Score</th><th>Classification</th><th>Orders</th><th>Avg Order Value</th><th>Product Mix</th><th>Actual (USD)</th><th>Potential (USD)</th><th>Opportunity Gap</th><th>Growth % (QoQ)</th></tr></thead>
          <tbody>
            <tr v-for="p in filtered" :key="p.customerId" class="clickable" @click="openDrawer(p)">
              <td class="cell-strong">{{ p.pharmacyName }}</td><td class="cell-muted">{{ p.territory }}</td><td class="cell-muted">{{ p.category }}</td>
              <td class="cell-muted">{{ p.potentialScore }}</td><td><span class="tier-badge" :class="p.potentialClassification">{{ p.potentialClassification }}</span></td>
              <td class="cell-muted">{{ p.orderCount }}</td>
              <td class="cell-muted">{{ p.avgOrderValue ? '$'+p.avgOrderValue.toLocaleString() : '—' }}</td>
              <td class="cell-muted">
                <template v-if="p.divisionCount">{{ p.topDivision }} {{ p.topDivisionPct }}%<span v-if="p.divisionCount>1"> · {{ p.divisionCount }} divisions</span></template>
                <template v-else>—</template>
              </td>
              <td class="cell-muted">${{ p.purchaseValue.toLocaleString() }}</td><td class="cell-muted">${{ p.potentialValue.toLocaleString() }}</td>
              <td :style="p.opportunityGapValue>0?'color:var(--color-accent);font-weight:600;':'color:var(--status-green);'">{{ p.opportunityGapValue>0?'$'+p.opportunityGapValue.toLocaleString():'Realized' }}</td>
              <td :style="growthTone(p.growthPct).cls">{{ growthTone(p.growthPct).label }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <DetailDrawer :open="!!drawerPharmacy" :title="drawerPharmacy?.pharmacyName || ''" @close="drawerCustomerId=null">
      <template v-if="drawerPharmacy">
        <div class="eyebrow" style="margin-bottom:10px;">Purchase Value — trailing windows</div>
        <div class="stat-row" style="margin-bottom:16px;">
          <div class="card stat-card" style="box-shadow:none;"><div class="stat-value">${{ drawerPharmacy.monthlyValue.toLocaleString() }}</div><div class="stat-label">Monthly (last 1 mo.)</div></div>
          <div class="card stat-card" style="box-shadow:none;"><div class="stat-value">${{ drawerPharmacy.quarterlyValue.toLocaleString() }}</div><div class="stat-label">Quarterly (last 3 mo.)</div></div>
          <div class="card stat-card" style="box-shadow:none;"><div class="stat-value">${{ drawerPharmacy.annualValue.toLocaleString() }}</div><div class="stat-label">Annual (12 mo.)</div></div>
          <div class="card stat-card" style="box-shadow:none;"><div class="stat-value" :style="growthTone(drawerPharmacy.growthPct).cls">{{ growthTone(drawerPharmacy.growthPct).label }}</div><div class="stat-label">Growth % (QoQ)</div></div>
        </div>

        <div class="eyebrow" style="margin-bottom:8px;">12-Month Purchase Trend</div>
        <ChartView type="line" :labels="trendChart.labels" :datasets="trendChart.datasets" :height="200" :key="'trend-'+drawerPharmacy.customerId" />

        <div class="eyebrow" style="margin:20px 0 10px;">Potential vs. Actual</div>
        <dl style="display:grid;grid-template-columns:auto 1fr;gap:8px 16px;margin:0;font-size:13px;">
          <dt class="cell-muted">Category / Classification</dt><dd style="margin:0">{{ drawerPharmacy.category }} — <span class="tier-badge" :class="drawerPharmacy.potentialClassification">{{ drawerPharmacy.potentialClassification }}</span></dd>
          <dt class="cell-muted">Potential Score</dt><dd style="margin:0">{{ drawerPharmacy.potentialScore }} / 100</dd>
          <dt class="cell-muted">Avg Order Value</dt><dd style="margin:0">{{ drawerPharmacy.avgOrderValue ? '$'+drawerPharmacy.avgOrderValue.toLocaleString() : '—' }} across {{ drawerPharmacy.orderCount }} orders</dd>
          <dt class="cell-muted">Product Mix</dt><dd style="margin:0">{{ drawerPharmacy.divisionCount ? drawerPharmacy.topDivision + ' ' + drawerPharmacy.topDivisionPct + '%' + (drawerPharmacy.divisionCount>1 ? ' · '+drawerPharmacy.divisionCount+' divisions' : '') : '—' }}</dd>
          <dt class="cell-muted">Opportunity Gap</dt><dd style="margin:0" :style="drawerPharmacy.opportunityGapValue>0?'color:var(--color-accent);font-weight:600;':'color:var(--status-green);'">{{ drawerPharmacy.opportunityGapValue>0?'$'+drawerPharmacy.opportunityGapValue.toLocaleString()+' untapped':'Potential realized' }}</dd>
        </dl>
      </template>
    </DetailDrawer>
  </div>
</template>